import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';

import { Botao } from './Botao';

/**
 * A SEGUNDA CAMADA DO FORMULÁRIO (pedido do dono, 31/10/2026: as melhorias do
 * formulário de combustível levadas aos outros).
 *
 * O formulário tem duas camadas. À vista, o que se digita todo dia. O que o
 * sistema já respondeu — o depósito de sempre, o fornecedor da última nota, a
 * unidade do item — aparece como ETIQUETA, numa linha só, e os campos só
 * abrem no botão "Ajustar …". Nenhum campo some: o valor continua escrito na
 * tela para quem lança conferir, e o campo está a um clique.
 *
 * É o desenho do "Quem e onde" do abastecimento (`FormularioAbastecimento`),
 * em componente, para os formulários do estoque e das compras não copiarem a
 * marcação. A etiqueta é a da p.25: atributo, neutra, raio 8.
 */
export function LinhaDoPreenchido({
  rotulo,
  aberta,
  aoAlternar,
  rotuloDoBotao,
  idDoConteudo,
  children,
  aviso,
}: {
  /** O nome da linha, em rótulo de campo: "Onde e de quem". */
  readonly rotulo: string;
  /** Os campos de ajuste estão abertos. */
  readonly aberta: boolean;
  readonly aoAlternar: () => void;
  /** O que o botão abre, sem o verbo: "depósito e fornecedor" vira "Ajustar depósito e fornecedor". */
  readonly rotuloDoBotao: string;
  /** O id do bloco que o botão abre (`aria-controls`). */
  readonly idDoConteudo: string;
  /** As etiquetas (`EtiquetaDoPreenchido`). */
  readonly children: ReactNode;
  /** Uma frase de conferência, na linha de baixo, em âmbar. */
  readonly aviso?: string | null;
}) {
  return (
    <div className="campo-linha flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
      <span className="txt-rotulo-campo shrink-0">{rotulo}</span>
      {/* As etiquetas pedem 320 px antes de dividir a linha com o botão: na
          janela de 640 px o botão desce para a linha de baixo, à direita, em
          vez de espremer o nome do depósito até "A.". */}
      <div className="flex min-w-0 flex-[1_1_320px] flex-wrap items-center gap-2">{children}</div>
      <Botao className="ml-auto" nivel="fantasma" tamanho="p" aria-expanded={aberta} aria-controls={idDoConteudo} onClick={aoAlternar}>
        {aberta ? (
          <ChevronUp aria-hidden="true" className="size-[var(--icone-controle)]" />
        ) : (
          <ChevronDown aria-hidden="true" className="size-[var(--icone-controle)]" />
        )}
        {aberta ? `Fechar ${rotuloDoBotao}` : `Ajustar ${rotuloDoBotao}`}
      </Botao>
      {aviso ? <span className="txt-ajuda w-full text-atencao-texto">{aviso}</span> : null}
    </div>
  );
}

/**
 * A ETIQUETA DO QUE O SISTEMA RESPONDEU: o rótulo em cinza na frente e o
 * valor. A marcada pelo sistema ganha "· pré-preenchido", como o campo
 * ganharia; a vazia fica no apagado, para "Sem fornecedor" não parecer um
 * fornecedor chamado assim.
 */
export function EtiquetaDoPreenchido({
  rotulo,
  valor,
  vazia = false,
  marcada = false,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly vazia?: boolean;
  readonly marcada?: boolean;
}) {
  return (
    <span className="inline-flex min-h-[var(--selo-altura)] max-w-full items-center gap-1 rounded-etiqueta bg-rebaixada px-2 py-0.5 txt-selo">
      <span className="shrink-0 text-tinta-secundaria">{rotulo}</span>
      <span className={`truncate ${vazia ? 'text-tinta-apagada' : 'text-tinta'}`}>{valor}</span>
      {marcada ? <span className="shrink-0 text-tinta-apagada">· pré-preenchido</span> : null}
    </span>
  );
}

/**
 * O BOTÃO QUE REVELA O RARO: observação, dados do fabricante, embalagem. Só
 * o botão fica à vista; o campo nasce quando a pessoa pede. Com o campo já
 * preenchido, ele fica aberto (quem escreveu não pode perder de vista o que
 * escreveu).
 */
export function BotaoDoRaro({
  aberto,
  aoAlternar,
  rotulo,
  rotuloAberto,
  idDoConteudo,
}: {
  readonly aberto: boolean;
  readonly aoAlternar: () => void;
  /** Com verbo: "Escrever observação", "Mais dados do item". */
  readonly rotulo: string;
  readonly rotuloAberto: string;
  readonly idDoConteudo: string;
}) {
  return (
    <div className="campo-linha flex min-w-0 flex-wrap items-center gap-2">
      <Botao nivel="fantasma" tamanho="p" aria-expanded={aberto} aria-controls={idDoConteudo} onClick={aoAlternar}>
        {aberto ? (
          <ChevronUp aria-hidden="true" className="size-[var(--icone-controle)]" />
        ) : (
          <ChevronDown aria-hidden="true" className="size-[var(--icone-controle)]" />
        )}
        {aberto ? rotuloAberto : rotulo}
      </Botao>
    </div>
  );
}
